"use client"

import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { MetricCard } from '../components/reports/MetricCard';
import { CategoryMarketSharePieCharts } from '../components/reports/CategoryMarketSharePieCharts';
import html2pdf from 'html2pdf.js';
import { Download, Lock, TrendingUp } from 'lucide-react';

type CampaignReport = {
  id: string;
  brandId: string;
  campaignName: string;
  reportType: 'new-brand' | 'existing-brand' | 'mid-campaign';
  campaignStartDate: string;
  campaignEndDate?: string;
  reportDate?: string;
  status: 'draft' | 'published' | 'archived';
  createdAt: string;
  updatedAt: string;
  Brand?: {
    id: string;
    name: string;
  };
  creator?: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
  reportData?: string; // JSON string
  metrics?: Array<{
    id: string;
    metricType: string;
    metricCategory: string;
    period: 'pre-campaign' | 'campaign' | 'post-campaign';
    value: number | null;
    percentageValue: number | null;
    metadata?: string | null;
  }>;
};

interface ReportData {
  // Campaign Overview
  campaignName?: string;
  brandId?: string;
  reportDate?: string;
  campaignStartDate?: string;
  campaignEndDate?: string;
  daysElapsed?: string;
  daysRemaining?: string;
  campaignId?: string;
  
  // Media Performance
  totalImpressions?: string;
  averageImpressionsPerMachine?: string;
  targetImpressions?: string;
  doohAdPlays?: string;
  averagePlaysPerMachine?: string;
  totalScreenTimeMinutes?: string;
  homepageBannerImpressions?: string;
  categoryBannerImpressions?: string;
  clickThroughRate?: string;
  productPageViews?: string;
  engagementRate?: string;
  
  // Sales Performance
  overallSalesPercentIncrease?: string;
  products?: Array<{ id: string; name: string; percentIncrease: string }>;
  
  // Category Performance - Market Share by Product
  preCampaignMarketShareProducts?: Array<{ id: string; name: string; marketShare: string }>;
  postCampaignMarketShareProducts?: Array<{ id: string; name: string; marketShare: string }>;
  currentMarketShareProducts?: Array<{ id: string; name: string; marketShare: string }>;
  
  // Demographics
  genderMale?: string;
  genderFemale?: string;
  age1824?: string;
  age2534?: string;
  age3544?: string;
  age4554?: string;
  age55Plus?: string;
  peakHours?: string;
  peakDays?: string;
  purchaseTimingMorning?: string;
  purchaseTimingAfternoon?: string;
  purchaseTimingEvening?: string;
  
  // Campaign Pacing & Budget
  totalCampaignBudget?: string;
  percentCampaignElapsed?: string;
  percentBudgetUtilized?: string;
  pacingStatus?: 'on-track' | 'ahead' | 'behind';
  
  // Key Insights
  keyInsights?: string;
}

export default function SharedReportView() {
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const [report, setReport] = useState<CampaignReport | null>(null);
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [requiresPassword, setRequiresPassword] = useState(false);
  const [passwordVerified, setPasswordVerified] = useState(false);
  const reportContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (token) {
      checkReportAccess();
    }
  }, [token]);

  async function checkReportAccess() {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/reports/shared/${token}`);
      if (res.ok) {
        const data = await res.json();
        setRequiresPassword(!!data.hasPassword);
        if (!data.hasPassword) {
          // No password required, load report directly
          await loadReport('');
        }
      } else {
        alert('Report not found or link is invalid');
      }
    } catch (error) {
      console.error('Failed to check report access:', error);
      alert('Failed to load report');
    } finally {
      setLoading(false);
    }
  }

  async function loadReport(passwordToUse?: string) {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/reports/shared/${token}/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password: passwordToUse !== undefined ? passwordToUse : password || undefined }),
      });

      if (res.ok) {
        const data = await res.json();
        setReport(data);
        setPasswordVerified(true);
        
        // Parse reportData JSON or convert metrics to reportData format
        if (data.reportData) {
          try {
            const parsed = typeof data.reportData === 'string' 
              ? JSON.parse(data.reportData) 
              : data.reportData;
            setReportData(parsed);
          } catch (e) {
            console.error('Failed to parse reportData:', e);
          }
        } else if (data.metrics && data.metrics.length > 0) {
          // CSV-based report: Convert metrics to reportData format
          const getMetricValue = (category: string, period: string, field: string): number | undefined => {
            const metric = data.metrics?.find(
              (m: any) => m.metricType === field && m.metricCategory === category && m.period === period
            );
            return metric?.value ?? metric?.percentageValue ?? undefined;
          };
          
          const convertedData: ReportData = {
            totalImpressions: getMetricValue('conversion', 'campaign', 'total-impressions')?.toString(),
            homepageBannerImpressions: getMetricValue('conversion', 'campaign', 'homepage-banner-impressions')?.toString(),
            categoryBannerImpressions: getMetricValue('conversion', 'campaign', 'category-banner-impressions')?.toString(),
            productPageViews: getMetricValue('conversion', 'campaign', 'products-browsed')?.toString(),
            doohAdPlays: getMetricValue('engagement', 'campaign', 'total-ad-plays')?.toString(),
            averagePlaysPerMachine: getMetricValue('engagement', 'campaign', 'average-plays-per-machine')?.toString(),
            totalScreenTimeMinutes: getMetricValue('engagement', 'campaign', 'total-screen-time-minutes')?.toString(),
            overallSalesPercentIncrease: getMetricValue('sales-performance', 'campaign', 'sales-increase-percentage')?.toString(),
          };
          
          setReportData(convertedData);
        }
      } else {
        const error = await res.json();
        if (error.requiresPassword) {
          setPasswordError('Invalid password');
        } else {
          alert(error.message || 'Failed to load report');
        }
      }
    } catch (error) {
      console.error('Failed to load report:', error);
      alert('Failed to load report');
    } finally {
      setLoading(false);
    }
  }

  async function handlePasswordSubmit(e: React.FormEvent) {
    e.preventDefault();
    setPasswordError('');
    await loadReport(password);
  }

  async function handleExportPDF() {
    if (!report || !reportData) return;
    
    if (!reportContentRef.current) {
      alert('Report content not found. Please try again.');
      return;
    }
    
    try {
      // Hide header buttons during export
      const headerElement = document.getElementById('shared-report-header');
      const originalDisplay = headerElement?.style.display || '';
      if (headerElement) {
        headerElement.style.display = 'none';
      }
      
      const element = reportContentRef.current;
      const opt = {
        margin: [15, 15, 15, 15] as [number, number, number, number],
        filename: `${report.campaignName || 'Report'}-${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg' as const, quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          logging: false,
          letterRendering: true,
          backgroundColor: '#ffffff',
        },
        jsPDF: { 
          unit: 'mm' as const, 
          format: 'a4' as const, 
          orientation: 'portrait' as const,
          compress: true,
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] as any }
      };
      
      await html2pdf().set(opt).from(element).save();
      
      // Restore header visibility
      if (headerElement) {
        headerElement.style.display = originalDisplay;
      }
    } catch (error: any) {
      console.error('Failed to export PDF:', error);
      alert(`Failed to export PDF: ${error.message || 'Unknown error'}`);
      
      // Restore header visibility on error
      const headerElement = document.getElementById('shared-report-header');
      if (headerElement) {
        headerElement.style.display = '';
      }
    }
  }

  if (loading) {
    return <div className="container mx-auto p-6">Loading...</div>;
  }

  if (requiresPassword && !passwordVerified) {
    return (
      <div className="container mx-auto p-6 max-w-md">
        <Card>
          <CardHeader>
            <CardTitle>Password Required</CardTitle>
            <CardDescription>
              This report is password protected. Please enter the password to view it.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  required
                />
                {passwordError && (
                  <p className="text-sm text-destructive">{passwordError}</p>
                )}
              </div>
              <Button type="submit" className="w-full">
                <Lock className="h-4 w-4 mr-2" />
                Unlock Report
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!report || !reportData) {
    return <div className="container mx-auto p-6">Report not found</div>;
  }

  const data = reportData;

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      {/* Header - Hidden during PDF export */}
      <div className="flex items-center justify-between mb-6" id="shared-report-header">
        <div>
          <h1 className="text-3xl font-bold">{report.campaignName || 'Untitled Campaign'}</h1>
          {report.Brand?.name && (
            <p className="text-muted-foreground mt-1">{report.Brand.name}</p>
          )}
        </div>
        <Button variant="outline" onClick={handleExportPDF}>
          <Download className="h-4 w-4 mr-2" />
          Export PDF
        </Button>
      </div>

      {/* Report Content - This will be exported */}
      <div ref={reportContentRef} className="report-content">
        {/* Campaign Overview */}
        <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-foreground">{report.campaignName || 'Campaign Overview'}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <div className="text-sm text-muted-foreground mb-1">Campaign Dates</div>
              <div className="flex items-center gap-2">
                <span>
                  {report.campaignStartDate ? new Date(report.campaignStartDate).toLocaleDateString() : 'N/A'}
                  {report.campaignEndDate ? ` - ${new Date(report.campaignEndDate).toLocaleDateString()}` : ''}
                </span>
              </div>
            </div>
            {data.reportDate && (
              <div>
                <div className="text-sm text-muted-foreground mb-1">Report Date</div>
                <div className="flex items-center gap-2">
                  <span>{new Date(data.reportDate).toLocaleDateString()}</span>
                </div>
              </div>
            )}
            {data.daysElapsed && (
              <div>
                <div className="text-sm text-muted-foreground mb-1">Days Elapsed</div>
                <div className="text-2xl font-bold">{data.daysElapsed}</div>
              </div>
            )}
            {data.daysRemaining && (
              <div>
                <div className="text-sm text-muted-foreground mb-1">Days Remaining</div>
                <div className="text-2xl font-bold">{data.daysRemaining}</div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Media Performance */}
      {(data.totalImpressions || data.homepageBannerImpressions || data.categoryBannerImpressions || data.productPageViews || data.doohAdPlays || data.averagePlaysPerMachine || data.totalScreenTimeMinutes) && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-foreground">Media Performance</CardTitle>
            <CardDescription>Impressions and digital engagement metrics</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {data.totalImpressions && (
                <MetricCard
                  title="Total Impressions"
                  value={parseInt(data.totalImpressions)}
                  label="Impressions"
                />
              )}
              
              {data.homepageBannerImpressions && (
                <MetricCard
                  title="Home Banner Ad Views"
                  value={parseInt(data.homepageBannerImpressions)}
                  label="Views"
                />
              )}
              
              {data.categoryBannerImpressions && (
                <MetricCard
                  title="Category Banner Ad Views"
                  value={parseInt(data.categoryBannerImpressions)}
                  label="Views"
                />
              )}
              
              {data.productPageViews && (
                <div>
                  <div className="text-sm text-muted-foreground mb-2">Product Page Views</div>
                  <div className="text-3xl font-bold">{parseInt(data.productPageViews).toLocaleString()}</div>
                </div>
              )}
            </div>
            
            {/* Total Ad Playout (To-Date) */}
            {(data.doohAdPlays || data.averagePlaysPerMachine || data.totalScreenTimeMinutes) && (
              <>
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4 text-foreground">Total Ad Playout (To-Date)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {data.doohAdPlays && (
                      <MetricCard
                        title="DOOH Ad Plays"
                        value={parseInt(data.doohAdPlays)}
                        label="Plays"
                      />
                    )}
                    {data.averagePlaysPerMachine && (
                      <MetricCard
                        title="Customer Dwell Time"
                        value={parseInt(data.averagePlaysPerMachine)}
                        label="Hours"
                      />
                    )}
                    {data.totalScreenTimeMinutes && (
                      <MetricCard
                        title="Total Screen Time"
                        value={parseInt(data.totalScreenTimeMinutes)}
                        label="Hours"
                      />
                    )}
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Sales Performance */}
      {data.overallSalesPercentIncrease && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-foreground">Sales Performance</CardTitle>
            <CardDescription>Unit sales metrics and product breakdown</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <div className="text-sm text-muted-foreground">Overall Sales Increase</div>
                <div className="text-3xl font-bold text-green-600">
                  +{parseFloat(data.overallSalesPercentIncrease).toFixed(1)}%
                </div>
              </div>
              <TrendingUp className="h-8 w-8 text-green-600" />
            </div>
            
            {data.products && data.products.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-4 text-foreground">Product Performance</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {data.products.map((product) => (
                    <Card key={product.id}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm text-muted-foreground mb-1">{product.name || 'Unnamed Product'}</div>
                            <div className="text-2xl font-bold text-green-600">
                              {product.percentIncrease ? `+${parseFloat(product.percentIncrease).toFixed(1)}%` : 'N/A'}
                            </div>
                          </div>
                          {product.percentIncrease && (
                            <TrendingUp className="h-8 w-8 text-green-600" />
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Category Performance - Market Share Pie Charts */}
      {data.preCampaignMarketShareProducts && data.preCampaignMarketShareProducts.length > 0 && (
        <CategoryMarketSharePieCharts
          preCampaignProducts={data.preCampaignMarketShareProducts.map(p => ({
            id: p.id,
            name: p.name,
            marketShare: parseFloat(p.marketShare) || 0,
          }))}
          currentProducts={(data.currentMarketShareProducts || data.postCampaignMarketShareProducts || data.preCampaignMarketShareProducts).map(p => ({
            id: p.id,
            name: p.name,
            marketShare: parseFloat(p.marketShare) || 0,
          }))}
          brandName={report.Brand?.name || 'Brand'}
        />
      )}

      {/* Demographics */}
      {(data.genderMale || data.age1824 || data.peakHours) && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-foreground">Demographics Insights</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {(data.genderMale || data.genderFemale) && (
              <div>
                <h3 className="text-lg font-semibold mb-4 text-foreground">Gender Split</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {data.genderMale && (
                    <div className="text-center p-4 bg-muted rounded-lg">
                      <div className="text-2xl font-bold">{parseFloat(data.genderMale).toFixed(1)}%</div>
                      <div className="text-sm text-muted-foreground">Male</div>
                    </div>
                  )}
                  {data.genderFemale && (
                    <div className="text-center p-4 bg-muted rounded-lg">
                      <div className="text-2xl font-bold">{parseFloat(data.genderFemale).toFixed(1)}%</div>
                      <div className="text-sm text-muted-foreground">Female</div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {(data.age1824 || data.age2534 || data.age3544) && (
              <div>
                <h3 className="text-lg font-semibold mb-4 text-foreground">Age Distribution</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {data.age1824 && (
                    <div className="text-center">
                      <div className="text-xl font-bold">{parseFloat(data.age1824).toFixed(1)}%</div>
                      <div className="text-xs text-muted-foreground">18-24</div>
                    </div>
                  )}
                  {data.age2534 && (
                    <div className="text-center">
                      <div className="text-xl font-bold">{parseFloat(data.age2534).toFixed(1)}%</div>
                      <div className="text-xs text-muted-foreground">25-34</div>
                    </div>
                  )}
                  {data.age3544 && (
                    <div className="text-center">
                      <div className="text-xl font-bold">{parseFloat(data.age3544).toFixed(1)}%</div>
                      <div className="text-xs text-muted-foreground">35-44</div>
                    </div>
                  )}
                  {data.age4554 && (
                    <div className="text-center">
                      <div className="text-xl font-bold">{parseFloat(data.age4554).toFixed(1)}%</div>
                      <div className="text-xs text-muted-foreground">45-54</div>
                    </div>
                  )}
                  {data.age55Plus && (
                    <div className="text-center">
                      <div className="text-xl font-bold">{parseFloat(data.age55Plus).toFixed(1)}%</div>
                      <div className="text-xs text-muted-foreground">55+</div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {(data.peakHours || data.peakDays || data.purchaseTimingMorning) && (
              <div>
                <h3 className="text-lg font-semibold mb-4 text-foreground">Purchase Timing</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {data.peakHours && (
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">Peak Hours</div>
                      <div className="font-semibold">{data.peakHours}</div>
                    </div>
                  )}
                  {data.peakDays && (
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">Peak Days</div>
                      <div className="font-semibold">{data.peakDays}</div>
                    </div>
                  )}
                  {(data.purchaseTimingMorning || data.purchaseTimingAfternoon || data.purchaseTimingEvening) && (
                    <div>
                      <div className="text-sm text-muted-foreground mb-2">Time Distribution</div>
                      <div className="space-y-1">
                        {data.purchaseTimingMorning && (
                          <div className="flex justify-between">
                            <span>Morning:</span>
                            <span className="font-semibold">{parseFloat(data.purchaseTimingMorning).toFixed(1)}%</span>
                          </div>
                        )}
                        {data.purchaseTimingAfternoon && (
                          <div className="flex justify-between">
                            <span>Afternoon:</span>
                            <span className="font-semibold">{parseFloat(data.purchaseTimingAfternoon).toFixed(1)}%</span>
                          </div>
                        )}
                        {data.purchaseTimingEvening && (
                          <div className="flex justify-between">
                            <span>Evening:</span>
                            <span className="font-semibold">{parseFloat(data.purchaseTimingEvening).toFixed(1)}%</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Campaign Pacing & Budget */}
      {(data.totalCampaignBudget || data.percentCampaignElapsed || data.percentBudgetUtilized) && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-foreground">Campaign Pacing & Budget</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {data.totalCampaignBudget && (
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Total Campaign Budget</div>
                  <div className="text-2xl font-bold">£{parseFloat(data.totalCampaignBudget).toLocaleString()}</div>
                </div>
              )}
              {data.percentCampaignElapsed && (
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Campaign Elapsed</div>
                  <div className="text-2xl font-bold">{parseFloat(data.percentCampaignElapsed).toFixed(1)}%</div>
                </div>
              )}
              {data.percentBudgetUtilized && (
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Budget Utilized</div>
                  <div className="text-2xl font-bold">{parseFloat(data.percentBudgetUtilized).toFixed(1)}%</div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Key Insights */}
      {data.keyInsights && (
        <Card>
          <CardHeader>
            <CardTitle className="text-foreground">Key Insights & Observations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="prose max-w-none whitespace-pre-wrap">{data.keyInsights}</div>
          </CardContent>
        </Card>
      )}
      </div>
    </div>
  );
}

